import React from 'react';
import Select from 'react-select';
import AvailabilityPlannerService from '../../../Services/AvailabilityPlannerService';
import OfficeService from '../../../Services/OfficeService';
import AuthService from '../../../Services/authService';
import ErrorMessage from '../../../Elements/ErrorMessage';
import Loading from '../../../Elements/loading';
import FaIcon from '../../../Elements/FaIcon';
import dateTimeHelper from '../../../Helpers/datetimeHelper';
import AvailabilityPlannerTable from './AvailabilityPlannerTable';

// Team Grid: an office-scoped, read-only overview of everyone's planned availability. Offices are filtered on
// the backend (they change the queried set); departments are filtered in the browser (they only narrow the
// already-loaded rows). An empty selection in either filter means "everything".
export default class AvailabilityPlannerTeamGrid extends React.Component {
  constructor(props) {
    super(props);
    const loggedInUser = AuthService.getInstance().GetLoggedInUser();
    const defaultOffice = (loggedInUser && loggedInUser.officeId) || 0;
    this.state = {
      offices: [],
      officeIds: defaultOffice ? [defaultOffice] : [],
      selectedDepartments: [],
      year: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      users: [],
      loading: true,
    };
  }

  componentDidMount() {
    OfficeService.GetOfficesForSelect()
      .then((offices) => this.setState({ offices: Array.isArray(offices) ? offices : [] }))
      .catch(() => ErrorMessage.SetErrorMessage('APG28'));
    this.load();
  }

  load() {
    this.setState({ loading: true });
    AvailabilityPlannerService.GetOfficeMonth(this.state.officeIds, this.state.year, this.state.month)
      .then((data) => this.setState({ users: Array.isArray(data) ? data : [] }))
      .catch(() => ErrorMessage.SetErrorMessage('APG27'))
      .finally(() => this.setState({ loading: false }));
  }

  changeMonth(delta) {
    let month = this.state.month + delta;
    let year = this.state.year;
    if (month < 1) {
      month = 12;
      year -= 1;
    } else if (month > 12) {
      month = 1;
      year += 1;
    }
    this.setState({ month, year }, () => this.load());
  }

  changeOffices(officeIds) {
    this.setState({ officeIds: Array.isArray(officeIds) ? officeIds : [] }, () => this.load());
  }

  officeOptions() {
    return this.state.offices.map((o) => ({ value: o.officeId, label: o.name }));
  }

  // The distinct departments present in the loaded rows — the department filter's options.
  departmentOptions() {
    const set = new Set();
    this.state.users.forEach((u) => set.add(u.departmentName || 'Unassigned'));
    return [...set].sort((a, b) => a.localeCompare(b)).map((d) => ({ value: d, label: d }));
  }

  // Client-side department filter. Empty selection = every department.
  filteredUsers() {
    if (this.state.selectedDepartments.length === 0) {
      return this.state.users;
    }
    const selected = new Set(this.state.selectedDepartments);
    return this.state.users.filter((u) => selected.has(u.departmentName || 'Unassigned'));
  }

  renderControls() {
    const officeOptions = this.officeOptions();
    const officeValue = officeOptions.filter((o) => this.state.officeIds.includes(o.value));
    const deptOptions = this.departmentOptions();
    const deptValue = deptOptions.filter((d) => this.state.selectedDepartments.includes(d.value));
    // Render the dropdown menus in a portal so the surrounding layout can't clip them.
    const selectStyles = { menuPortal: (base) => ({ ...base, zIndex: 9999 }) };
    const menuPortalTarget = typeof document !== 'undefined' ? document.body : null;
    return (
      <>
        <div className='row p-1 align-items-center'>
          <div className='col-4' />
          <div className='col-1 text-center h6 mb-0'>
            <FaIcon
              iconName='fa fa-chevron-left'
              tooltipText='Previous month'
              onClick={() => this.changeMonth(-1)}
            />
          </div>
          <div className='col-2 text-center h6 mb-0'>
            {dateTimeHelper.MonthName(this.state.month - 1)} {this.state.year}
          </div>
          <div className='col-1 text-center h6 mb-0'>
            <FaIcon
              iconName='fa fa-chevron-right'
              tooltipText='Next month'
              onClick={() => this.changeMonth(1)}
            />
          </div>
          <div className='col-4 text-end'>
            <FaIcon
              iconName='fa fa-print'
              tooltipText='Print'
              onClick={() => window.print()}
            />
          </div>
        </div>
        <div className='row p-1'>
          <div className='col-md-6'>
            <div className='ap-filter-label'>Offices (none = all)</div>
            <Select
              isMulti
              closeMenuOnSelect={false}
              options={officeOptions}
              value={officeValue}
              onChange={(sel) => this.changeOffices((sel || []).map((o) => o.value))}
              placeholder='All offices'
              menuPortalTarget={menuPortalTarget}
              styles={selectStyles}
            />
          </div>
          <div className='col-md-6'>
            <div className='ap-filter-label'>Departments (none = all)</div>
            <Select
              isMulti
              closeMenuOnSelect={false}
              options={deptOptions}
              value={deptValue}
              onChange={(sel) => this.setState({ selectedDepartments: (sel || []).map((d) => d.value) })}
              placeholder='All departments'
              menuPortalTarget={menuPortalTarget}
              styles={selectStyles}
            />
          </div>
        </div>
      </>
    );
  }

  render() {
    return (
      <div>
        {this.renderControls()}
        {this.state.loading ? (
          <Loading />
        ) : (
          <AvailabilityPlannerTable
            users={this.filteredUsers()}
            year={this.state.year}
            month={this.state.month}
            grouped={true}
            readOnly={true}
          />
        )}
      </div>
    );
  }
}
